import { BaseRemoteService } from "@ofqual/portal-core/core/dataAccess";
import {
  Opts,
  Model,
  QueryParams,
  PagedResponse,
  JSONPatchDocument,
  ValidatedResponse,
} from "@ofqual/portal-core/core/types";
import { paramsToSearchString } from "@ofqual/portal-core/core/utils/urlHelper";
// import { Attachment } from "../domain/Attachment";
import { PagedResponseTo } from "@ofqual/portal-core/core/utils/pagedResponseHelpers";
import { FoiItem, fromApiModel } from "../domain/FoiItem";
import { getValidationError } from "@ofqual/portal-core/core/utils/validationHelper";
import { FoiEvent, FoiEventComplete } from "../domain/FoiEvent";

export class FoiService extends BaseRemoteService {
  public async get(id: string, opts: Opts): Promise<FoiItem> {
    const url = `${this.apiRoot}/foiItems/${id.toString()}`;
    const response = await super.getRequest<Model>(url, opts);
    return fromApiModel(response);
  }

  public async search(
    params: QueryParams,
    opts: Opts
  ): Promise<PagedResponse<FoiItem>> {
    const url = `${this.apiRoot}/foiItems/${paramsToSearchString(params)}`;
    const response = await super.getRequest<PagedResponse<Model>>(url, opts);
    return PagedResponseTo<FoiItem>(response, fromApiModel);
  }

  public async update(
    id: string,
    patchDocument: JSONPatchDocument,
    opts: Opts
  ): Promise<ValidatedResponse<FoiItem>> {
    const url = `${this.apiRoot}/foiItems/${id}`;
    try {
      const response = await this.patchRequest<FoiItem>(
        url,
        patchDocument,
        opts
      );
      return {
        isValid: true,
        data: fromApiModel(response),
      };
    } catch (err) {
      console.log(err);
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return {
          isValid: false,
          error: validationError.error,
        };
      }
      throw err;
    }
  }

  public async create(
    item: FoiItem,
    opts: Opts
  ): Promise<ValidatedResponse<FoiItem>> {
    const url = `${this.apiRoot}/foiItems`;

    try {
      const response: FoiItem = await super.postRequest(url, item, opts);

      return {
        isValid: true,
        data: response,
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async addInternalMessage(
    id: string,
    files: File[],
    subject: string,
    message: string,
    recipients: string[],
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/foiItems/${id}/internal-message`;
    const messageData = { id, subject, message, recipients };
    const messageSerialized = JSON.stringify(messageData);
    return await super.postRequestFormData(
      url,
      { messageSerialized, files },
      opts
    );
  }

  public async addAddPublicMessage(
    id: string,
    files: File[],
    subject: string,
    message: string,
    responseTypeId: number,
    formalResponseTypeId: number | null,
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/foiItems/${id}/public-message-ofqual`;
    const messageData = { id, subject, message, responseTypeId, formalResponseTypeId };
    const messageSerialized = JSON.stringify(messageData);
    return await super.postRequestFormData(
      url,
      { messageSerialized, files },
      opts
    );
  }

  public async downloadActivityAttachment(
    id: string,
    activityId: string,
    blobId: string,
    opts: Opts
  ): Promise<Blob> {
    const url = `${this.apiRoot}/foiItems/${id}/documents/${activityId}/download/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async downloadFoiEventAttachment(
    id: string,
    eventId: string | null | undefined,
    blobId: string | null | undefined,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/foiItems/${id}/event/${eventId}/document/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async assignFoi(
    id: string,
    upn: string,
    displayName: string,
    opts: Opts
  ): Promise<FoiItem> {
    const url = `${this.apiRoot}/foiItems/${id}/assign`;
    const messageData = { id, upn, displayName };
    return await super.postRequest(url, messageData, opts);
  }

  public async unassignFoi(id: string, opts: Opts): Promise<FoiItem> {
    const url = `${this.apiRoot}/foiItems/${id}/unassign`;
    return await super.postRequest(url, {}, opts);
  }

  public async closeFoi(id: string, opts: Opts): Promise<FoiItem> {
    const url = `${this.apiRoot}/foiItems/${id}/close`;
    return await super.postRequest(url, {}, opts);
  }

  public async cancelFoi(id: string, opts: Opts): Promise<FoiItem> {
    const url = `${this.apiRoot}/foiItems/${id}/cancel`;
    return await super.postRequest(url, {}, opts);
  }

  public async reOpenFoi(id: string, opts: Opts): Promise<FoiItem> {
    const url = `${this.apiRoot}/foiItems/${id}/reopen`;
    return await super.postRequest(url, {}, opts);
  }

  public async updateStage(
    id: string,
    foiStage: string,
    opts: Opts
  ): Promise<FoiItem> {
    const url = `${this.apiRoot}/foiItems/setStage/${id}?stage=${foiStage}`;
    return await super.postRequest(url, {}, opts);
  }

  public async downloadAttachment(id: string, blobId: string, opts: Opts) {
    const url = `${this.apiRoot}/foiItems/${id}/attachments/${blobId}/download`;
    return await super.getRequestBlob(url, opts);
  }

  public async putFoiEventByItemId(
    foiId: string,
    event: FoiEvent,
    opts: Opts
  ): Promise<FoiEvent> {
    const url = `${this.apiRoot}/foiItems/${foiId}/event`;

    const formData = {
      id: event.id,
      foiId: foiId,
      message: event.message,
      recipients: event.recipients,
      foiEventType: event.foiEventType,
      meetingDate: event.meetingDate,
      notes: event.notes,
      eventStage: 0,
      wasAttended: false,
    };
    const metaJson = JSON.stringify(formData);

    return await super.postRequestFormData<FoiEvent>(
      url,
      {
        files: event.files,
        metaSerialised: metaJson,
      },
      opts
    );
  }

  public async completeFoiEventByItemId(
    foiId: string,
    foiEventId: string,
    eventCompletion: FoiEventComplete,
    opts: Opts
  ): Promise<FoiEventComplete> {
    const url = `${this.apiRoot}/foiItems/${foiId}/event/complete`;

    const formData = {
      foiId: foiId,
      id: foiEventId,
      action: eventCompletion.action,
      notes: eventCompletion.notes,
    };
    const metaJson = JSON.stringify(formData);

    return await super.postRequestFormData<FoiEventComplete>(
      url,
      {
        files: eventCompletion.files,
        metaSerialised: metaJson,
      },
      opts
    );
  }

  public async addCaseNote(
    id: string,
    caseFiles: File[],
    caseNote: string,
    subject: string,
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/foiItems/${id}/casenote`;
    return await super.postRequestFormData(
      url,
      { caseNote, caseFiles, subject },
      opts
    );
  }

  public async deleteAttachment(
    id: string,
    blobId: string,
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/foiItems/${id}/attachments/${blobId}`;
    return await super.deleteRequest(url, opts);
  }

  public async postAttachments(
    id: string,
    attachments: File[],
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/foiItems/${id}/attachments`;
    return await super.postRequestFormData(url, { files: attachments }, opts);
  }

  public async getSlaConfig(opts: Opts): Promise<Model[]>{
    const url = `${this.apiRoot}/foiItems/slaConfig`;
    const response = await super.getRequest<Model[]>(url, opts);
    const data: Model[] = [response];
    return (data);
  }
}
